
import React from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { FormItem, FormLabel, FormControl, FormDescription, FormMessage } from '@/components/ui/form';
import { FormInputCore } from './FormInputCore';
import { getValidationRules } from './FormInputTypes';

export interface FormInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  name: string;
  label?: string;
  description?: string;
  showError?: boolean;
  fullWidth?: boolean;
  containerClassName?: string;
  labelClassName?: string;
  isRequired?: boolean;
  validateOnBlur?: boolean;
  validateOnChange?: boolean;
  validation?: Record<string, any>;
  inputType?: 'text' | 'numeric' | 'alphabets' | 'decimal' | 'tel' | 'email' | 'url';
}

export function FormInput({
  name,
  label,
  description,
  showError = true,
  fullWidth = true,
  containerClassName,
  labelClassName,
  isRequired = false,
  validateOnBlur = true,
  validateOnChange = false,
  validation = {},
  inputType = 'text',
  placeholder,
  className,
  ...props
}: FormInputProps) {
  const form = useFormContext();
  
  // Generate validation rules based on inputType and isRequired
  const validationRules = {
    ...getValidationRules(inputType, isRequired),
    ...validation
  };

  if (form) {
    return (
      <Controller
        name={name}
        control={form.control}
        rules={validationRules}
        render={({ field, fieldState: { error } }) => (
          <FormItem>
            {label && (
              <FormLabel 
                htmlFor={`form-input-${name}`} 
                className={isRequired ? 'after:content-["*"] after:ml-0.5 after:text-destructive' : ''}
              >
                {label}
              </FormLabel>
            )}
            <FormControl>
              <FormInputCore
                {...field}
                {...props}
                name={name}
                inputType={inputType}
                isRequired={isRequired}
                className={className}
                error={error?.message}
                label={undefined}
                containerClassName=""
                fullWidth={false}
                value={field.value || ''}
                placeholder={placeholder || `Enter ${name.charAt(0).toUpperCase() + name.slice(1)}`}
                onBlur={(e) => {
                  field.onBlur();
                  props.onBlur?.(e);
                  if (validateOnBlur) {
                    form.trigger(name);
                  }
                }}
                onChange={(e) => {
                  // Pass the event to field.onChange
                  field.onChange(e);
                  props.onChange?.(e);
                  if (validateOnChange) {
                    form.trigger(name);
                  }
                }}
              />
            </FormControl>
            {description && <FormDescription>{description}</FormDescription>}
            {showError && error?.message && (
              <FormMessage>{error.message}</FormMessage>
            )}
          </FormItem>
        )}
      />
    );
  }

  // Non-form context version
  return (
    <FormInputCore
      name={name}
      label={label}
      description={description}
      inputType={inputType}
      isRequired={isRequired}
      className={className}
      fullWidth={fullWidth}
      containerClassName={containerClassName}
      labelClassName={labelClassName}
      placeholder={placeholder || `Enter ${name.charAt(0).toUpperCase() + name.slice(1)}`}
      {...props}
    />
  );
}
